<script setup>
// "New club member" from inside another flow (meet Add members / Add to
// register): the same ClubMemberForm + POST players as the Members page, but
// it hands the created player back to whoever opened it so they can carry on
// (tick them, add them to the date) without leaving.
import {computed, ref, watch} from 'vue';
import ClubMemberForm from '~/components/admin/ClubMemberForm.vue';

const props = defineProps({
  clubId: {type: String, required: true},
});
const emit = defineEmits(['created']);
const open = defineModel({type: Boolean, default: false});

const {item, createItem, formErrors, formPending, hasFormErrors} = useCrudApi('api/v2/players');

const blank = () => ({firstName: '', lastName: '', telephone: '', email: '', noUser: false, isJunior: false});
const draft = ref(blank());
const created = ref(null);

watch(open, (isOpen) => {
  if (isOpen) {
    draft.value = blank();
    created.value = null;
    formErrors.value = {};
  }
});

// Same normalisation as the Members page: clubId, blank/admin-managed email → null.
const save = async () => {
  if (created.value) {
    // Second press = "Done" on the share-link view.
    open.value = false;
    return;
  }
  const email = (draft.value.email || '').trim();
  const body = {
    ...draft.value,
    clubId: props.clubId,
    email: (draft.value.noUser || email === '') ? null : email,
  };
  await createItem(body);
  if (hasFormErrors.value) return;
  created.value = item.value;
  emit('created', item.value);
  // Name-only members get a share link to hand over — show it before closing.
  if (!item.value?.inviteShortUrl) open.value = false;
};

// ClubMemberForm shows the share-link view when the model has inviteShortUrl+id.
const formModel = computed(() => created.value ?? draft.value);
</script>

<template>
  <crud-form-dialog
      v-model:dialog="open"
      :model-value="formModel"
      action="create"
      create-title="Add new member"
      :btn-text="created ? 'Done' : 'Add member'"
      :btn-icon="created ? 'mdi-check' : 'mdi-account-plus'"
      :form-errors="formErrors"
      :form-pending="formPending"
      :save-action="save"
      :max-dialog-width="700"
  >
    <template #default>
      <club-member-form
          :model-value="formModel"
          :errors="formErrors"
          action="create"
      />
    </template>
  </crud-form-dialog>
</template>
